import { useState } from "react";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { Breadcrumb, Button } from "@/components";
import BeforeAfterSlider from "./BeforeAfterSlider";
import { cn, tinaField } from "@/utils";

export default function BeforeAfter({ beforeAfter, hasBackground }) {
  if (!beforeAfter?.cases?.length) return null;

  return (
    <section
      className={cn("space-y-6", hasBackground && "gray-section-background")}
      id={encodeURIComponent(beforeAfter.pretitle)}
    >
      <hgroup className="border-b border-dark/50 pb-3 space-y-2">
        <Breadcrumb label={beforeAfter.pretitle} data-tina-field={tinaField(beforeAfter, "pretitle")} size="sm" />
        <h2 className="h2" data-tina-field={tinaField(beforeAfter, "title")}>
          {beforeAfter.title}
        </h2>
      </hgroup>
      <div className="space-y-4">
        {beforeAfter.cases.map((item, index) => (
          <div
            className="flex flex-col lg:flex-row gap-3 lg:gap-6 py-4 border-b border-dark/30 last:border-0 last:pb-0 first:pt-0"
            key={index}
          >
            <div
              className="flex-1 font-bold text-[17px]"
              data-tina-field={tinaField(beforeAfter, `cases.${index}.title`)}
            >
              {item.title}
            </div>
            <div className="flex-[3] space-y-6">
              {item.description && (
                <p data-tina-field={tinaField(beforeAfter, `cases.${index}.description`)}>{item.description}</p>
              )}
              <CaseCarousel caseItem={item} caseIndex={index} beforeAfter={beforeAfter} />
            </div>
          </div>
        ))}
      </div>
    </section>
  );
}

function CaseCarousel({ caseItem, caseIndex, beforeAfter }) {
  const pairs = caseItem?.pairs ?? [];
  const [activeIndex, setActiveIndex] = useState(0);
  const pair = pairs[activeIndex];
  const hasMultiple = pairs.length > 1;

  if (!pairs.length) return null;

  const goTo = (direction) => {
    setActiveIndex((current) => Math.min(pairs.length - 1, Math.max(0, current + direction)));
  };

  return (
    <div className="space-y-8">
      <BeforeAfterSlider
        pair={pair}
        beforeAlt={pair?.caption ? `${pair.caption} before` : `${caseItem.title} before`}
        afterAlt={pair?.caption ? `${pair.caption} after` : `${caseItem.title} after`}
      />
      {pair?.caption && !/^case\s*\d+$/i.test(pair.caption.trim()) && (
        <p className="text-sm text-dark/70" data-tina-field={tinaField(beforeAfter, `cases.${caseIndex}.pairs.${activeIndex}.caption`)}>
          {pair.caption}
        </p>
      )}
      {hasMultiple && (
        <div className="flex items-center gap-4 pb-4">
          <div className="flex items-center gap-2.5">
            <Button
              type="button"
              size="icon"
              className="lg:h-10 lg:w-10 h-8 w-8"
              onClick={() => goTo(-1)}
              disabled={activeIndex === 0}
              aria-label="Previous case"
            >
              <ChevronLeft className="h-4 w-4" />
            </Button>
            <Button
              type="button"
              size="icon"
              className="lg:h-10 lg:w-10 h-8 w-8"
              onClick={() => goTo(1)}
              disabled={activeIndex === pairs.length - 1}
              aria-label="Next case"
            >
              <ChevronRight className="h-4 w-4" />
            </Button>
          </div>
          <span className="text-sm font-semibold text-dark/60">
            Case {activeIndex + 1} of {pairs.length}
          </span>
        </div>
      )}
    </div>
  );
}
